import { useCanRedo, useHistory, useIsInsideRoom } from "@liveblocks/react"
import { useEffect, useRef } from "react"
import { type Options as HotKeysOptions, useHotkeys } from "react-hotkeys-hook"

export function HistoryProvider() {
  const isInsideRoom = useIsInsideRoom()

  if (!isInsideRoom) return null

  return <UndoRedoProvider />
}

function UndoRedoProvider() {
  const history = useHistory()

  useHotkeys(
    "mod+z",
    (event) => {
      event.preventDefault()
      history.undo()
    },
    hotKeysOptions,
  )

  useHotkeys(
    "mod+shift+z",
    (event) => {
      event.preventDefault()
      history.redo()
    },
    hotKeysOptions,
  )

  return null
}

const hotKeysOptions: HotKeysOptions = {
  enableOnContentEditable: true, // To allow hotkey events to be from RTF
}

export function useIsLastRedoAction() {
  const canRedo = useMaybeCanRedo()
  const previousCanRedo = usePrevious(canRedo)
  const isLastRedoAction = previousCanRedo && previousCanRedo !== canRedo

  return isLastRedoAction
}

/**
 * useCanRedo but does not throw when it's not inside a RoomProvider
 * (which happens when `pb-collaboration-room` flag is off)
 */
function useMaybeCanRedo(): ReturnType<typeof useCanRedo> {
  try {
    return useCanRedo()
  } catch {
    return false
  }
}

/**
 * @private
 *
 * Tracks the previous value of a variable
 * @returns The previous value
 */
function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined)

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref.current
}
